import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FormInput from './FormInput';
import FormUtil from './FormUtil';
import FormHelpBlock from './FormHelpBlock';
import './FormInputMinMax.less';

class FormInputMinMax extends PureComponent {
    static displayName = 'FormInputMinMax';

    static propTypes = {
        /** The Input's id, required for a11y */
        id: PropTypes.string.isRequired,
        /** The Input's label, required for a11y */
        label: PropTypes.node.isRequired,
        /** The actual content of the input */
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        /** The type of data to be accepted by the Input. Add count condition based on your use case, for example, minimum count can be required while setting passwords */
        type: PropTypes.oneOf(['text', 'password', 'email', 'number', 'tel']),
        /** Want to pass a CSS class to the input? This is your property. */
        inputClassName: PropTypes.string,
        /** For form controls with no visible label, set this to true. Make sure to still add a value to the label property. For screen readers. */
        srOnlyLabel: PropTypes.bool,
        /** Add content to the left of the text. This can be a string, element, or component */
        leftContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        /** Add content to the right side of the input box. This can be a string, element, or component */
        rightContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        /** If you are sticking content into the input that needs to accept mouse interactions, set this to true. */
        contentClickable: PropTypes.bool,
        /** The help message displayed beneath the input. */
        helpText: PropTypes.node,
        /** The error message displayed beneath the input. Styling of the input will visually indicate an error state. */
        errorMsg: PropTypes.node,
        /** Flag to make helpText and errorMsg use white text for better a11y contrast when used on dark backgrounds */
        inverse: PropTypes.bool,
        /** Called when the value property changes */
        onChange: PropTypes.func,
        /** Enables character counter and sets maximum count (0 indicates no maximum and will not enable count display)*/
        maxChars: PropTypes.number,
        /** Enables character counter and sets minimum count (0 indicates no minimum and will not enable count display)*/
        minChars: PropTypes.number,
        /** Flag when set to true will only render character count related messages when there is a character count error state*/
        onlyShowMsgOnError: PropTypes.bool,
        /** Prop to support localization of the string being used to communicate character count. It supports interpolation by replacing {minChars} or {maxChars}. */
        lingua: PropTypes.shape({
            minCharMessage: PropTypes.string,
            minCharErrorMessage: PropTypes.string,
            maxCharErrorMessage: PropTypes.string
        })
    };

    static defaultProps = {
        srOnlyLabel: false,
        contentClickable: false,
        onlyShowMsgOnError: false,
        type: 'text',
        inputClassName: 'FormInputMinMax__input',
        maxChars: 0,
        minChars: 0,
        lingua: {
            minCharMessage: '{minChars} character minimum',
            minCharErrorMessage: 'Characters remaining: {minChars}',
            maxCharErrorMessage: 'Over character limit'
        }
    };

    constructor(props) {
        super(props);
        this.state = {
            hasBeenBlurred: false
        };
    }

    handleFocus = (event) => {
        if (!this.state.hasFocus) {
            this.setState({hasFocus: true});
        }

        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    }

    handleBlur = (event) => {
        if (this.state.hasFocus) {
            this.setState({
                hasFocus: false,
                hasBeenBlurred: true
            });
        }

        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    }

    render() {
        const {helpText, errorMsg, maxChars, minChars, onlyShowMsgOnError, lingua, ...rest} = this.props;
        const {hasFocus, hasBeenBlurred} = this.state;
        const id = this.props.id;

        // note that below, the disabled and value props have not been destructured since they need
        // to get passed through with ...rest to the input element to be used as an attribute

        const helpBlockValues = FormUtil.computeHelpValues({
            id,
            disabled: this.props.disabled,
            value: this.props.value,
            helpText,
            onlyShowMsgOnError,
            errorMsg,
            maxChars,
            minChars,
            lingua,
            hasBeenBlurred,
            hasFocus,
            inverse: this.props.inverse
        });

        const {maxCharCountExceeded, hasError} = helpBlockValues;
        const parentClasses = classNames('FormInputMinMax', {'has-error': hasError});

        const inputProps = {
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,
            // Computing which errors are being displayed and assigning respective aria-describedby values
            ...(FormUtil.needAriaMsg({
                errorMsg,
                helpText,
                minChars,
                maxCharCountExceeded
            }) && {'aria-describedby': FormUtil.computeAriaMsgList({
                id,
                errorMsg,
                helpText,
                minChars,
                maxCharCountExceeded
            })}),
            ...rest
        };

        return (
            <div className={parentClasses}>
                <FormInput {...inputProps}/>
                <FormHelpBlock {...helpBlockValues} />
            </div>
        );
    }
}

export default FormInputMinMax;
